// The names and descriptions of what companions are, have and do, as the menu shows them: English
// from the engine and the designs, any other language from its table (i18n/<code>.json). Brand
// names stay as they are (Kirby, Steve); folklore names take each language's own (Santa).
//
// The keys, for translators:
//   companion.<id>.name        a companion's name                 companion.santa.name: Santa
//   companion.<id>.about       its description                    companion.santa.about: Santa Claus, round …
//   group.<Group>              a group of companions               group.Critters: Critters
//   action.<id>                an action from engine/catalog.js     action.nap: Naps when you're away
//   ability.<name>             an ability's own label (its own ways  ability.sleigh: Flies in his sleigh
//                              to travel and things to do, and how   ability.inhale: Inhales it
//                              it walks, fights and sleeps)
//   style.walk.<style>         how it walks, the engine's own ways  style.walk.profile: Walks side-on
//   style.attack.<style>       how it fights pests (sword, flee)    style.attack.sword: Swings a sword
//   style.sleep.<style>        how it sleeps (bed, sit, curl …)     style.sleep.bed: Sleeps in a bed
//   skin.<key>                 a skin every companion has           skin.rainbow: Rainbow
//   skin.<id>.<key>            a companion's own skin               skin.santa.nikolaus: St. Nikolaus
//   hat.<key>, clothes.<key>   an outfit                            hat.crown: Crown
//   pattern.<key>              a parachute's pattern                pattern.dots: Polka dots
//   level.<key>                a behavior level (and still)         level.background: Background
//   size.<step>                a companion's size next to others    size.-2: Tiny
//   say.<English>              what a companion says in a bubble    say.Ho ho ho!: Ho ho ho!
//                              (engine/speech.js), keyed by the
//                              English as written
// tools/strings.mjs collects every one of them, with the menu's (ui/strings.js), into
// i18n/source/en.json, the English the translators work from, and checks each language's table.
import { tr } from '../engine/i18n.js';
import { attackStyle, sleepStyle, walkStyle } from '../engine/catalog.js';
import { ability } from '../engine/abilities.js';
import { SKINS, lookFor } from '../engine/skins.js';
import { OUTFITS } from '../engine/outfits.js';
import { CHUTE_PATTERNS } from '../engine/props.js';
import { SIZES } from '../engine/companion.js';
import { ACTIVITY, OWN_LEVELS } from '../engine/activity.js';

// The order the groups are listed in (a design's "group"; others last).
export const GROUP_ORDER = ['Originals', 'Critters', 'Holidays', 'Apps', 'Mascots', 'Games', 'Classics', 'Mythology', 'Minecraft'];

// The engine's own ways of walking, fighting and sleeping (the others are abilities with a label).
export const WALKS = { walk: 'Walks', profile: 'Walks side-on', hop: 'Hops', fly: 'Flies', roll: 'Rolls', none: 'Doesn’t walk' };
export const ATTACKS = { sword: 'Swings a sword', flee: 'Runs away and calls for help' };
export const SLEEPS = { bed: 'Sleeps in a bed', sit: 'Dozes off where it is', curl: 'Curls up', lie: 'Lies down', float: 'Floats asleep' };
// The screen's behavior levels, and a companion's own: those and Still, which only a companion can be.
export const SCREEN_LEVELS = Object.keys(ACTIVITY);
export const LEVELS = Object.keys(OWN_LEVELS);

export const companionName = design => tr(`companion.${design.id}.name`, design.name);
export const about = design => tr(`companion.${design.id}.about`, design.about || '');
export const groupName = group => tr(`group.${group}`, group);
// An action from the catalog, or one of the design's own (an ability: its label).
export const actionName = action => (action.own ? tr(`ability.${action.id}`, action.label) : tr(`action.${action.id}`, action.label));
const styleName = (kind, name, builtIn) => (Object.hasOwn(builtIn, name) ? tr(`style.${kind}.${name}`, builtIn[name])
  : tr(`ability.${name}`, ability(name)?.label || name));
export const walkName = design => styleName('walk', walkStyle(design), WALKS);
export const hopName = design => (design.moves?.hop ? tr(`ability.${design.moves.hop}`, ability(design.moves.hop)?.label || design.moves.hop) : '');
export const attackName = design => styleName('attack', attackStyle(design), ATTACKS);
export const sleepName = design => styleName('sleep', sleepStyle(design), SLEEPS);
export function skinName(design, key) {
  const label = lookFor(design, key).label;
  return SKINS[key] ? tr(`skin.${key}`, label) : tr(`skin.${design.id}.${key}`, label);
}
export const hatName = key => tr(`hat.${key}`, OUTFITS.head[key] ?? key);
export const clothesName = key => tr(`clothes.${key}`, OUTFITS.body[key] ?? key);
export const patternName = key => tr(`pattern.${key}`, CHUTE_PATTERNS[key] ?? key);
export const levelName = key => tr(`level.${key}`, OWN_LEVELS[key]?.label ?? key);
export const sizeName = step => tr(`size.${step}`, SIZES[step] ?? String(step));
